import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';

/**
 * Reflection Agent Service
 * Facilitates metacognitive reflection and learning insights
 */
export const ReflectionAgentService = {
  /**
   * Generate guided reflection prompts based on activity
   * @param {Object} activityData - Completed activity data
   * @returns {Promise<Array>} - Personalized reflection prompts
   */
  async generateReflectionPrompts(activityData) {
    const payload = {
      agentId: 'reflection',
      persona: activityData.persona || null,
      activity: {
        id: activityData.id,
        title: activityData.title || activityData.what,
        type: activityData.type || 'general',
        completed: activityData.completed,
        difficulty: activityData.difficulty || 'medium',
      },
      userContext: {
        userId: activityData.userId,
        learningGoals: activityData.learningGoals || [],
        previousReflections: activityData.previousReflections || [],
      },
      courseContext: activityData.courseContext || {},
    };

    const response = await fetch(`${API_BASE_URL}/reflection/generate-prompts`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.prompts;
  },

  /**
   * Analyze user's reflection and provide feedback
   * @param {Object} reflectionData - User's reflection content
   * @returns {Promise<Object>} - AI analysis and feedback
   */
  async analyzeReflection(reflectionData) {
    const payload = {
      agentId: 'reflection',
      persona: reflectionData.persona || null,
      reflection: reflectionData.reflectionText,
      activity: {
        id: reflectionData.activityId,
        title: reflectionData.activityTitle,
        type: reflectionData.activityType,
      },
      userProfile: reflectionData.userProfile || {},
      learningGoals: reflectionData.learningGoals || [],
    };

    const response = await fetch(`${API_BASE_URL}/reflection/analyze`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      insights: data.insights,
      strengths: data.strengths,
      areasForGrowth: data.areasForGrowth,
      suggestions: data.suggestions,
      depthScore: data.depthScore,
    };
  },

  /**
   * Get learning insights from multiple reflections over time
   * @param {Object} reflectionHistory - User's reflection history
   * @returns {Promise<Object>} - Longitudinal learning insights
   */
  async getLearningInsights(reflectionHistory) {
    const payload = {
      agentId: 'reflection',
      persona: reflectionHistory.persona || null,
      userId: reflectionHistory.userId,
      timeframe: reflectionHistory.timeframe || 'month',
      reflections: reflectionHistory.reflections || [],
      activities: reflectionHistory.activities || [],
      learningGoals: reflectionHistory.learningGoals || [],
    };

    const response = await fetch(`${API_BASE_URL}/reflection/learning-insights`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      patterns: data.patterns,
      growthAreas: data.growthAreas,
      recommendations: data.recommendations,
      milestones: data.milestones,
      metacognitiveScore: data.metacognitiveScore,
    };
  },

  /**
   * Generate a reflection summary for a learning period
   * @param {Object} periodData - Data for the learning period
   * @returns {Promise<string>} - AI-generated summary
   */
  async generatePeriodSummary(periodData) {
    const payload = {
      agentId: 'reflection',
      persona: periodData.persona || null,
      userId: periodData.userId,
      startDate: periodData.startDate,
      endDate: periodData.endDate,
      completedActivities: periodData.completedActivities || [],
      reflections: periodData.reflections || [],
      achievements: periodData.achievements || [],
      challenges: periodData.challenges || [],
    };

    const response = await fetch(`${API_BASE_URL}/reflection/period-summary`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.summary;
  },

  /**
   * Chat with Reflection AI Coach
   * @param {string} userMessage - User's message
   * @param {Object} context - Reflection context and chat history
   * @returns {Promise<Object>} - AI coach response
   */
  async chatWithReflectionCoach(userMessage, context) {
    const payload = {
      agentId: 'reflection',
      persona: context.persona || null,
      message: userMessage,
      userId: context.userId,
      userName: context.userName || 'Student',
      userProfile: context.userProfile || {},
      chatHistory: context.chatHistory || [],
      reflectionContext: {
        currentActivity: context.currentActivity || null,
        recentReflections: context.recentReflections || [],
        learningGoals: context.learningGoals || [],
      },
    };

    const response = await fetch(`${API_BASE_URL}/reflection/chat`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      sender: 'Reflection Coach',
      text: data.response,
      timestamp: new Date().toLocaleTimeString(),
      followUpQuestions: data.followUpQuestions || [],
      resources: data.resources || [],
    };
  },

  /**
   * Get AI-suggested reflection exercises
   * @param {Object} userContext - User context for suggestions
   * @returns {Promise<Array>} - List of reflection exercises
   */
  async suggestReflectionExercises(userContext) {
    const payload = {
      agentId: 'reflection',
      persona: userContext.persona || null,
      userId: userContext.userId,
      learningStyle: userContext.learningStyle || 'balanced',
      currentChallenges: userContext.currentChallenges || [],
      learningGoals: userContext.learningGoals || [],
      recentActivities: userContext.recentActivities || [],
    };

    const response = await fetch(`${API_BASE_URL}/reflection/suggest-exercises`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.exercises;
  },

  /**
   * Generate comprehensive daily journal entry from all day's data
   * @param {Object} dailyData - All data from the user's day
   * @returns {Promise<Object>} - AI-generated journal entry with insights
   */
  async generateDailyJournal(dailyData) {
    const payload = {
      agentId: 'reflection',
      persona: dailyData.persona || null,
      userId: dailyData.userId,
      date: dailyData.date || new Date().toISOString(),

      // Tasks and activities data
      tasksData: {
        completed: dailyData.completedTasks || [],
        pending: dailyData.pendingTasks || [],
        created: dailyData.createdTasks || [],
        totalTimeSpent: dailyData.totalTimeSpent || 0,
      },

      // Learning activities
      learningData: {
        coursesStudied: dailyData.coursesStudied || [],
        lessonsCompleted: dailyData.lessonsCompleted || [],
        assessmentsTaken: dailyData.assessmentsTaken || [],
        notesCreated: dailyData.notesCreated || [],
      },

      // User reflections and responses
      userResponses: {
        reflectionAnswers: dailyData.reflectionAnswers || [],
        chatInteractions: dailyData.chatInteractions || [],
        freeformNotes: dailyData.freeformNotes || '',
      },

      // Life context (non-learning activities)
      lifeContext: {
        mood: dailyData.mood || null,
        energyLevel: dailyData.energyLevel || null,
        significantEvents: dailyData.significantEvents || [],
        challenges: dailyData.challenges || [],
        wins: dailyData.wins || [],
        gratitude: dailyData.gratitude || [],
      },

      // Goals and progress
      goalsData: {
        learningGoals: dailyData.learningGoals || [],
        personalGoals: dailyData.personalGoals || [],
        progressTowardsGoals: dailyData.progressTowardsGoals || [],
      },

      // Context from previous entries
      previousEntries: dailyData.recentJournalEntries || [],
    };

    const response = await fetch(`${API_BASE_URL}/reflection/generate-daily-journal`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      journalEntry: data.journalEntry,
      insights: data.insights,
      patterns: data.patterns,
      recommendations: data.recommendations,
      emotionalTone: data.emotionalTone,
      keyMoments: data.keyMoments,
      gratitudeSummary: data.gratitudeSummary,
      tomorrowFocus: data.tomorrowFocus,
    };
  },

  /**
   * Save journal entry to database
   * @param {Object} journalData - Journal entry data to save
   * @returns {Promise<Object>} - Saved journal entry with ID
   */
  async saveJournalEntry(journalData) {
    const payload = {
      agentId: 'reflection',
      persona: journalData.persona || null,
      userId: journalData.userId,
      date: journalData.date,
      journalEntry: journalData.journalEntry,
      insights: journalData.insights || [],
      patterns: journalData.patterns || [],
      emotionalTone: journalData.emotionalTone || 'neutral',
      keyMoments: journalData.keyMoments || [],
      gratitudeSummary: journalData.gratitudeSummary || '',
      tomorrowFocus: journalData.tomorrowFocus || [],
      rawData: journalData.rawData || {},
    };

    const response = await fetch(`${API_BASE_URL}/reflection/save-journal`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.journal;
  },

  /**
   * Get journal entries for a date range
   * @param {Object} queryParams - Query parameters
   * @returns {Promise<Array>} - List of journal entries
   */
  async getJournalEntries(queryParams) {
    const payload = {
      agentId: 'reflection',
      persona: queryParams.persona || null,
      userId: queryParams.userId,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate || new Date().toISOString(),
      includeInsights: queryParams.includeInsights !== false,
    };

    const response = await fetch(`${API_BASE_URL}/reflection/get-journals`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.journals;
  },

  /**
   * Update existing journal entry with additional reflections
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} - Updated journal entry
   */
  async updateJournalEntry(updateData) {
    const payload = {
      agentId: 'reflection',
      persona: updateData.persona || null,
      journalId: updateData.journalId,
      userId: updateData.userId,
      additionalReflections: updateData.additionalReflections || '',
      updatedInsights: updateData.updatedInsights || [],
      mood: updateData.mood || null,
    };

    const response = await fetch(`${API_BASE_URL}/reflection/update-journal`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.journal;
  },

  /**
   * Generate weekly reflection from daily journals
   * @param {Object} weekData - Week's journal data
   * @returns {Promise<Object>} - Weekly reflection summary
   */
  async generateWeeklyReflection(weekData) {
    const payload = {
      agentId: 'reflection',
      persona: weekData.persona || null,
      userId: weekData.userId,
      startDate: weekData.startDate,
      endDate: weekData.endDate,
      dailyJournals: weekData.dailyJournals || [],
      weeklyGoals: weekData.weeklyGoals || [],
    };

    const response = await fetch(`${API_BASE_URL}/reflection/generate-weekly-reflection`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return {
      weeklyReflection: data.weeklyReflection,
      weeklyPatterns: data.weeklyPatterns,
      achievements: data.achievements,
      challenges: data.challenges,
      growthAreas: data.growthAreas,
      nextWeekFocus: data.nextWeekFocus,
    };
  },

  /**
   * Generate a refined journal entry from reflection form responses
   * @param {Object} reflectionData - Reflection form responses and context
   * @returns {Promise<Object>} - AI-generated journal entry
   */
  async generateJournalEntry(reflectionData) {
    // Format user responses into a readable context for the chat
    const responses = reflectionData.userResponses ?? {};
    const questions = reflectionData.questions ?? [];

    const formattedLines =
      questions.length > 0
        ? questions.map((question, index) => {
            const rawValue = responses[question.id];

            let displayValue;
            if (rawValue === undefined || rawValue === null || rawValue === '') {
              displayValue = 'Not provided';
            } else if (question.type === 'slider') {
              const min = question.min ?? 1;
              const max = question.max ?? 5;
              displayValue = `${rawValue} (scale ${min}-${max})`;
            } else {
              displayValue = String(rawValue);
            }

            return `${index + 1}. ${question.question || question.id}: ${displayValue}`;
          })
        : Object.entries(responses).map(([key, value], index) => {
            if (value === undefined || value === null || value === '') {
              return `${index + 1}. ${key}: Not provided`;
            }
            return `${index + 1}. ${key}: ${value}`;
          });

    const contextMessage = `Please write a refined, cohesive journal entry based on my reflection responses for ${
      reflectionData.date
    }:

${formattedLines.join('\n')}

Please synthesize these responses into a well-written, first-person journal entry that captures my learning experience, insights, and growth. Make it personal, reflective, and cohesive.`;

    const payload = {
      agentId: 'reflection',
      persona: reflectionData.persona || null,
      message: contextMessage,
      userId: reflectionData.userId ?? 'unknown-user',
      userName: 'Student',
      userProfile: reflectionData.userProfile ?? {},
      chatHistory: [],
      reflectionContext: {
        date: reflectionData.date,
        responses: reflectionData.userResponses,
      },
    };

    const response = await fetch(`${API_BASE_URL}/reflection/chat`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Handle case where response might be a JSON string
    let parsedData = data;
    if (typeof data.response === 'string') {
      try {
        parsedData = JSON.parse(data.response);
      } catch (e) {
        // If parsing fails, treat it as plain text
        parsedData = { response: data.response };
      }
    }

    // Extract the journal entry from various possible fields
    let journalText =
      parsedData.response ?? parsedData.journalEntry ?? parsedData.entry ?? data.response ?? '';

    // Clean up the journal entry text
    const cleanJournalEntry = journalText.replace(/\\n/g, '\n').replace(/\s+/g, ' ').trim();

    return {
      journalEntry: cleanJournalEntry,
      followUpQuestions: parsedData.followUpQuestions ?? data.followUpQuestions ?? [],
      resources: parsedData.resources ?? data.resources ?? [],
      insights: parsedData.insights ?? data.insights ?? [],
      keyThemes: parsedData.keyThemes ?? data.keyThemes ?? [],
      emotionalTone: parsedData.emotionalTone ?? data.emotionalTone ?? 'neutral',
    };
  },
};
